// Le choix des pictogrammes de poster.js. Déterministe, local, sans réseau :
// le même morceau donne toujours le même vivier, et il est calculé en une
// poignée de millisecondes au changement de piste, pas par image.
//
// Cinq sources en cascade, de la plus fondée à la plus faible :
//   0.9  LE TITRE      ce que le morceau dit de lui-même
//   0.6  LE GENRE      ce qu'on sait de sa famille
//   0.5  LE CARACTÈRE  ce qu'on ENTEND, sans aucune métadonnée
//   0.4  LA PALETTE    ce que la pochette montre
//   0.3  LE DÉFAUT     des formes, pour que le vivier soit toujours plein
//
// DEUX SOURCES DU BRIEF N'ONT PAS D'ALIMENTATION DANS CE DÉPÔT, et le code
// les porte quand même :
//   - LE GENRE. Il n'existe aucune taxonomie musicale ici ; `genre` est lu sur
//     l'entrée et vaut 'pop' à défaut. Le jour où radiopi en renvoie un, c'est
//     un champ de plus à passer, la table ci-dessous est déjà écrite.
//   - lowEnd, density et gridStrength. orchestrator.js les déclare, mais
//     density vaut percussiveness (la MÊME mesure, pas une seconde),
//     gridStrength vaut 1 ou 0 selon qu'une analyse existe, et lowEnd n'a
//     aucune source. Les régles qui s'en servent sont écrites et GARDÉES :
//     elles ne se déclenchent que sur une valeur continue qui n'est pas une
//     copie de percussiveness. Les brancher telles quelles ferait tirer
//     `square, repeat` sur tout morceau analysé, ce qui ressemblerait à une
//     décision alors que ce serait un booléen déguisé.
import vocabulaire from '../icons/vocabulary.json' with { type: 'json' };

const POIDS = { titre: 0.9, genre: 0.6, caractere: 0.5, palette: 0.4, defaut: 0.3 };
export const TAILLE_VIVIER = 8;
// Le bonus quand plusieurs mots du titre désignent le même pictogramme :
// « Summer Nights » nomme la nuit deux fois si on compte `night` et `nights`,
// et c'est un signal, pas du bruit.
const BONUS_CONVERGENCE = 0.2;
// Une correspondance par préfixe vaut moins qu'une correspondance exacte :
// « sunshine » vers `sun` est juste, « sunday » vers `sun` ne l'est pas, et
// cinq caractères est le seuil où le second cas devient rare.
const PREFIXE_MIN = 5;
const SCORE_EXACT = 1.0;
const SCORE_PREFIXE = 0.7;

// Mots qui ne disent rien du sujet du morceau. Les versions et les mentions
// d'édition en font partie : « Extended Club Mix » ne parle pas de boîte de
// nuit, il parle d'un format.
const VIDES = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'in', 'on', 'to', 'for', 'is', 'it',
  'de', 'du', 'des', 'la', 'le', 'les', 'un', 'une', 'et', 'ou', 'au', 'aux',
  'el', 'los', 'las', 'y', 'con', 'por', 'para',
  'il', 'lo', 'gli', 'e', 'di', 'da', 'per',
  'der', 'die', 'das', 'und', 'von', 'zu', 'im',
  'feat', 'ft', 'featuring', 'with', 'avec',
  'remix', 'edit', 'version', 'live', 'radio', 'mix', 'original', 'extended',
  'club', 'instrumental', 'acoustic', 'remaster', 'remastered', 'mono',
  'stereo', 'single', 'album', 'ep', 'bonus', 'track', 'pt', 'part', 'vol'
]);

// --- l'index des mots-clés --------------------------------------------------
// Un mot peut désigner PLUSIEURS pictogrammes : « night » est à la fois dans la
// famille `moon` et dans la famille `night`, et « tienda » est en espagnol la
// boutique et la tente. On ne tranche pas - les deux marquent des points, et
// c'est l'accumulation qui décide. Trancher arbitrairement rendrait le
// résultat dépendant de l'ordre des familles.
const INDEX = new Map();
for (const f of vocabulaire.familles) {
  for (const mot of f.keywords) {
    let l = INDEX.get(mot);
    if (!l) { l = []; INDEX.set(mot, l); }
    for (const i of f.icons) if (l.indexOf(i) < 0) l.push(i);
  }
}
// Les préfixes de PREFIXE_MIN caractères, préparés une fois. Sans cet index,
// « sunshine » imposerait un balayage des 2036 mots-clés à chaque token.
const INDEX_PREFIXE = new Map();
for (const [mot, icones] of INDEX) {
  if (mot.length < PREFIXE_MIN) continue;
  const p = mot.slice(0, PREFIXE_MIN);
  let l = INDEX_PREFIXE.get(p);
  if (!l) { l = []; INDEX_PREFIXE.set(p, l); }
  for (const i of icones) if (l.indexOf(i) < 0) l.push(i);
}

// --- le genre ---------------------------------------------------------------
// Six pictogrammes par genre. Aucune source ne les alimente aujourd'hui (voir
// l'en-tête) ; la table est là pour le jour où.
const GENRES = {
  pop: ['heart', 'star', 'sparkles', 'sun', 'music', 'smile'],
  disco: ['disc', 'sparkles', 'sun', 'heart', 'star', 'music'],
  house: ['circle', 'disc', 'sparkles', 'zap', 'waves', 'sun'],
  techno: ['square', 'grid-3x3', 'zap', 'circle', 'triangle', 'repeat'],
  'hip-hop': ['mic-vocal', 'crown', 'headphones', 'flame', 'coins', 'car'],
  rap: ['mic-vocal', 'crown', 'coins', 'flame', 'car', 'headphones'],
  trap: ['flame', 'coins', 'car', 'skull', 'mic-vocal', 'crown'],
  rock: ['guitar', 'zap', 'flame', 'skull', 'star', 'mic-vocal'],
  metal: ['skull', 'zap', 'flame', 'swords', 'guitar', 'mountain'],
  punk: ['zap', 'skull', 'flag', 'guitar', 'flame', 'scissors'],
  indie: ['feather', 'cloud', 'camera', 'book', 'leaf', 'star'],
  folk: ['leaf', 'trees', 'mountain', 'feather', 'house', 'sun'],
  country: ['mountain', 'trees', 'house', 'guitar', 'sun', 'route'],
  blues: ['guitar', 'moon', 'wine', 'cloud-rain', 'feather', 'mic-vocal'],
  jazz: ['music', 'moon', 'wine', 'building-2', 'coffee', 'feather'],
  soul: ['heart', 'mic-vocal', 'music', 'star', 'flame', 'moon'],
  rnb: ['heart', 'mic-vocal', 'moon', 'star', 'flame', 'music'],
  funk: ['disc', 'zap', 'sparkles', 'music', 'star', 'footprints'],
  classical: ['feather', 'music', 'columns', 'moon', 'book', 'star'],
  opera: ['crown', 'music', 'columns', 'feather', 'star', 'moon'],
  ambient: ['cloud', 'moon', 'wind', 'waves', 'feather', 'circle'],
  electro: ['zap', 'circle', 'square', 'grid-3x3', 'waves', 'disc'],
  electronic: ['zap', 'circle', 'square', 'grid-3x3', 'waves', 'disc'],
  trance: ['circle', 'star', 'waves', 'sparkles', 'sun', 'infinity'],
  'drum-and-bass': ['drum', 'zap', 'waves', 'repeat', 'activity', 'speaker'],
  dubstep: ['speaker', 'zap', 'waves', 'square', 'flame', 'drum'],
  garage: ['disc', 'circle', 'repeat', 'zap', 'music', 'waves'],
  dance: ['sparkles', 'disc-3', 'star', 'zap', 'heart', 'music'],
  edm: ['zap', 'sparkles', 'speaker', 'star', 'circle', 'flame'],
  reggae: ['sun', 'leaf', 'palmtree', 'waves', 'heart', 'music'],
  dub: ['speaker', 'waves', 'leaf', 'sun', 'moon', 'drum'],
  ska: ['sun', 'music', 'footprints', 'star', 'party-popper', 'leaf'],
  afrobeat: ['sun', 'drum', 'leaf', 'paw-print', 'heart', 'music'],
  latin: ['sun', 'flower', 'heart', 'wine', 'palmtree', 'music'],
  salsa: ['heart', 'flower', 'music', 'sun', 'wine', 'party-popper'],
  reggaeton: ['flame', 'heart', 'car', 'coins', 'sun', 'music'],
  kpop: ['star', 'heart', 'sparkles', 'crown', 'music', 'smile'],
  gospel: ['church', 'star', 'heart', 'music', 'sun', 'feather'],
  world: ['map', 'sun', 'leaf', 'drum', 'flower', 'music'],
  chanson: ['wine', 'coffee', 'book', 'heart', 'building-2', 'moon'],
  lofi: ['coffee', 'moon', 'cloud', 'book', 'bed', 'music'],
  soundtrack: ['camera', 'star', 'moon', 'mountain', 'feather', 'music'],
  disco_funk: ['disc', 'sparkles', 'star', 'music', 'heart', 'zap']
};
const GENRE_DEFAUT = 'pop';

const DEFAUTS = ['circle', 'square', 'triangle', 'star', 'zap', 'heart', 'arrow-right', 'waves'];

// --- normalisation ----------------------------------------------------------
export function normaliser(texte) {
  if (!texte) return [];
  return String(texte)
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')   // accents
    .replace(/['’`]/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 1 && !VIDES.has(t) && !/^\d+$/.test(t));
}

// --- les sources ------------------------------------------------------------

function sourceTitre(titre, artiste) {
  // LE PRÉFIXE NE S'APPLIQUE QU'AU TITRE. Un nom d'artiste est un nom propre :
  // « Montand » attrapait `montagne`, « Iglesias » attrapait `iglesia`, et
  // « Bocelli » n'est pas près de vouloir dire quoi que ce soit. Sur le titre
  // le préfixe rattrape une flexion, sur le nom il invente un sens. Les
  // correspondances EXACTES sur le nom restent : « Prince » vaut vraiment
  // couronne, « The Doors » vaut vraiment porte.
  const scores = new Map();
  const vus = new Map();
  const ajouter = (tokens, prefixeAutorise) => {
    for (const t of tokens) {
      let icones = INDEX.get(t);
      let score = SCORE_EXACT;
      if (!icones && prefixeAutorise && t.length >= PREFIXE_MIN) {
        icones = INDEX_PREFIXE.get(t.slice(0, PREFIXE_MIN));
        score = SCORE_PREFIXE;
      }
      if (!icones) continue;
      for (const i of icones) {
        const n = (vus.get(i) || 0) + 1;
        vus.set(i, n);
        const bonus = n > 1 ? BONUS_CONVERGENCE : 0;
        scores.set(i, Math.max(scores.get(i) || 0, score) + bonus);
      }
    }
  };
  ajouter(normaliser(titre), true);
  ajouter(normaliser(artiste), false);
  return scores;
}

function sourceCaractere(c) {
  const out = new Map();
  if (!c) return out;
  const add = (liste, force) => { for (const i of liste) out.set(i, Math.max(out.get(i) || 0, force)); };
  const p = c.percussiveness, d = c.dynamics, s = c.syncopation;
  if (p != null) {
    if (p > 0.6) add(['zap', 'drum'], p);
    else if (p < 0.3) add(['feather', 'wind'], 1 - p);
  }
  if (d != null && d > 0.6) add(['activity'], d);
  if (s != null && s > 0.6) add(['shuffle'], s);
  // GARDÉES. density est aujourd'hui percussiveness à l'identique et
  // gridStrength un booléen : on ne s'en sert que si un jour ils portent
  // autre chose. Voir l'en-tête.
  if (c.density != null && c.density !== c.percussiveness) {
    if (c.density > 0.65) add(['grid-3x3', 'layers'], c.density);
    else if (c.density < 0.35) add(['circle', 'minus'], 1 - c.density);
  }
  if (c.gridStrength != null && c.gridStrength > 0 && c.gridStrength < 1) {
    if (c.gridStrength > 0.7) add(['square', 'repeat'], c.gridStrength);
    else if (c.gridStrength < 0.4) add(['leaf', 'waves'], 1 - c.gridStrength);
  }
  if (c.lowEnd != null && c.lowEnd > 0.6) add(['speaker', 'waves'], c.lowEnd);
  return out;
}

function teinte(hex) {
  const r = ((hex >> 16) & 255) / 255, g = ((hex >> 8) & 255) / 255, b = (hex & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (d < 1e-6) return -1;                       // gris : pas de teinte
  let h;
  if (mx === r) h = ((g - b) / d + (g < b ? 6 : 0));
  else if (mx === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return h * 60;
}

function sourcePalette(p) {
  // UNE SEULE icône, comme le demande le brief : la pochette dit quelque chose
  // de l'ambiance, pas du sujet.
  if (!p) return new Map();
  const dom = (p.dominantColors && p.dominantColors[0]);
  const h = dom != null ? teinte(dom) : -1;
  const sat = p.saturation, lum = p.brightness;
  let choix = null;
  if (sat != null && sat < 0.15) choix = 'camera';              // noir et blanc
  else if (lum != null && lum < 0.18) choix = 'moon';           // très sombre
  else if (lum != null && lum > 0.78) choix = 'cloud';          // très claire
  else if (h >= 0) {
    if (h < 40 || h >= 330) choix = 'flame';                    // rouge/orange
    else if (h < 70) choix = 'sun';                             // jaune
    else if (h < 160) choix = 'leaf';                           // vert
    else if (h < 260) choix = 'waves';                          // bleu/cyan
    else choix = 'sparkles';                                    // violet/rose
  }
  return choix ? new Map([[choix, 1]]) : new Map();
}

// --- le vivier --------------------------------------------------------------

function verser(vivier, scores, source, poids, quota) {
  const tri = Array.from(scores.entries()).sort((a, b) =>
    (b[1] - a[1]) || (a[0] < b[0] ? -1 : 1));       // le nom départage : déterministe
  let n = 0;
  for (const [nom, force] of tri) {
    if (n >= quota || vivier.length >= TAILLE_VIVIER) break;
    if (vivier.some((e) => e.nom === nom)) continue;
    vivier.push({ nom, poids: poids * Math.min(1, force), source });
    n++;
  }
}

export function construireVivier(entree) {
  const e = entree || {};
  const scoresTitre = sourceTitre(e.title, e.artist);

  // RÈGLE DE COHÉRENCE. Un titre qui parle beaucoup impose son sujet et le
  // genre passe au second plan ; un titre muet laisse genre et caractère se
  // partager le vivier à parts égales.
  const nTitre = Math.min(scoresTitre.size, TAILLE_VIVIER);
  const quotaGenre = nTitre >= 4 ? 2 : 3;
  const quotaCar = nTitre >= 4 ? 2 : 3;

  const vivier = [];
  verser(vivier, scoresTitre, 'titre', POIDS.titre, TAILLE_VIVIER);

  const genre = (e.genre && GENRES[String(e.genre).toLowerCase()]) ? String(e.genre).toLowerCase() : GENRE_DEFAUT;
  const scoresGenre = new Map();
  GENRES[genre].forEach((nom, i) => scoresGenre.set(nom, 1 - i * 0.08));
  verser(vivier, scoresGenre, 'genre', POIDS.genre, quotaGenre);

  verser(vivier, sourceCaractere(e.character), 'caractere', POIDS.caractere, quotaCar);
  verser(vivier, sourcePalette(e.palette), 'palette', POIDS.palette, 1);

  const scoresDefaut = new Map();
  DEFAUTS.forEach((nom, i) => scoresDefaut.set(nom, 1 - i * 0.05));
  verser(vivier, scoresDefaut, 'defaut', POIDS.defaut, TAILLE_VIVIER);

  return vivier;
}

// progress -> combien du vivier est actif, de 4 à 8. Les plus fondés d'abord.
export function actives(vivier, progress) {
  const p = progress == null ? 0.5 : Math.max(0, Math.min(1, progress));
  const n = Math.max(4, Math.min(TAILLE_VIVIER, Math.round(4 + p * 4)));
  return vivier.slice(0, n);
}

// Tirage pondéré : les icônes les mieux fondées sortent plus souvent, aucune
// n'est exclue. `eviter` est le dernier nom sorti de CE côté - jamais deux
// fois de suite le même au même endroit.
export function tirer(liste, rng, eviter) {
  const candidats = liste.length > 1 ? liste.filter((e) => e.nom !== eviter) : liste;
  let total = 0;
  for (const e of candidats) total += e.poids;
  let r = (rng ? rng() : Math.random()) * total;
  for (const e of candidats) { r -= e.poids; if (r <= 0) return e.nom; }
  return candidats[candidats.length - 1].nom;
}

export const VOCABULAIRE = vocabulaire;
